/**
 * compileCharacter.js — pure, no DOM/fetch
 *
 * compileCharacterBehavior(bodyGraph) -> { behaviorJson, usedNodeTypes, states, clips }
 *
 * Walks the body blueprint graph, validates all node types against the Slice-1
 * supported sets, collects state names and clip names, and returns a normalized
 * behaviorJson that the runtime (evaluateBody / GraphEvaluator) can consume.
 */

const SUPPORTED = new Set([
  'finalPose',
  'playClip',
  'blend',
  'select',
  'getVariable',
  'random',
  'stateMachine',
  'entry',
  'state',
  'stateOutput',
])

const SUPPORTED_RULES = new Set([
  'varGet',
  'ruleCompare',
  'ruleNot',
  'ruleAnd',
  'ruleOr',
  'ruleResult',
  'ruleAnimTimeRemaining',
  'ruleAnimTimeRatio',
])

/**
 * Validate a rule graph (from a transition edge's data.ruleGraph).
 * Throws if any node type is outside SUPPORTED_RULES.
 */
function validateRuleGraph(ruleGraph) {
  if (!ruleGraph || !Array.isArray(ruleGraph.nodes)) return
  for (const node of ruleGraph.nodes) {
    if (!SUPPORTED_RULES.has(node.type)) {
      throw new Error(
        `compileCharacterBehavior: unsupported rule node type "${node.type}"`
      )
    }
  }
}

/**
 * Walk a list of nodes (inner graph or top-level), validate types, collect
 * clips and states. Recursively descends into stateMachine innerNodes and
 * state innerNodes, and validates ruleGraphs on edges.
 *
 * @param {Array}  nodes        - node array to walk
 * @param {Array}  edges        - edge array for the same graph level
 * @param {Set}    usedTypes    - accumulates used node types
 * @param {Set}    clips        - accumulates clip names
 * @param {Set}    states       - accumulates SM state names
 */
function walkNodes(nodes, edges, usedTypes, clips, states) {
  if (!Array.isArray(nodes)) return

  for (const node of nodes) {
    const { type, data } = node

    if (!SUPPORTED.has(type)) {
      throw new Error(
        `compileCharacterBehavior: unsupported node type "${type}"`
      )
    }
    usedTypes.add(type)

    // Collect clips from playClip nodes
    if (type === 'playClip' && data?.clipName) {
      clips.add(data.clipName)
    }

    // Collect clips from state nodes (data.clipName or data.clips[])
    if (type === 'state') {
      const stateName = data?.name || node.id
      states.add(stateName)

      if (data?.clipName) {
        clips.add(data.clipName)
      }
      if (Array.isArray(data?.clips)) {
        for (const c of data.clips) {
          if (c && typeof c === 'string') clips.add(c)
          else if (c?.clipName) clips.add(c.clipName)
        }
      }

      // Descend into state's inner graph
      if (Array.isArray(data?.innerNodes)) {
        walkNodes(data.innerNodes, data.innerEdges || [], usedTypes, clips, states)
      }
    }

    // Descend into stateMachine inner graph
    if (type === 'stateMachine') {
      if (Array.isArray(data?.innerNodes)) {
        walkNodes(data.innerNodes, data.innerEdges || [], usedTypes, clips, states)
      }
      // Validate rule graphs on SM transition edges
      if (Array.isArray(data?.innerEdges)) {
        for (const edge of data.innerEdges) {
          if (edge?.data?.ruleGraph) {
            validateRuleGraph(edge.data.ruleGraph)
          }
        }
      }
    }
  }
}

/**
 * Compile and validate a body blueprint graph.
 *
 * @param {{ nodes: Array, edges: Array }} bodyGraph
 * @returns {{ behaviorJson: object, usedNodeTypes: string[], states: Set<string>, clips: Set<string> }}
 */
export function compileCharacterBehavior(bodyGraph) {
  if (!bodyGraph || !Array.isArray(bodyGraph.nodes)) {
    throw new Error('compileCharacterBehavior: bodyGraph must have a nodes array')
  }

  const usedTypes = new Set()
  const clips = new Set()
  const states = new Set()

  walkNodes(bodyGraph.nodes, bodyGraph.edges || [], usedTypes, clips, states)

  // Also validate any top-level edge rule graphs (unusual but possible)
  if (Array.isArray(bodyGraph.edges)) {
    for (const edge of bodyGraph.edges) {
      if (edge?.data?.ruleGraph) {
        validateRuleGraph(edge.data.ruleGraph)
      }
    }
  }

  // behaviorJson is the validated graph — shallow-normalized but runtime-consumable
  const behaviorJson = {
    nodes: bodyGraph.nodes,
    edges: bodyGraph.edges || [],
  }

  return {
    behaviorJson,
    usedNodeTypes: [...usedTypes],
    states,
    clips,
  }
}
